import React, {useContext, useEffect} from "react"
import {WidgetMap} from "./widget-map"
import {DataContext} from "../context/DataContextProvider"
import {getMerkmal} from "../../anwendungskonfigurator/misc/widget-functions"
import {
    checkConditionsForDisabled,
    checkConditionsForHidden,
    checkVisibility,
} from "../../anwendungskonfigurator/misc/precondition-evaluation"
import {ActionContext} from "../context/ActionContextProvider"
import {ContextAreaContext} from "../context/ContextAreaContext"
import {useDebounceTime} from "../../anwendungskonfigurator/misc/use-debounce-time"
import {defaultFor} from "../../anwendungskonfigurator/misc/default-registry"

const Widget = ({widget, type, payload, disabled, widgetValue}) => {
    const {getDataFromPointer, updateData} = useContext(DataContext)
    const {triggerAction} = useContext(ActionContext)
    const inheritedContextArea = useContext(ContextAreaContext)
    const {style} = widget
    const component = type ?? widget.component
    const merkmal = getMerkmal(widget)
    const savePointer = merkmal?.pointer
    const errorPointer = `/messages/error${merkmal?.pointer}`
    const value = widgetValue ?? getDataFromPointer(savePointer)
    const error = getDataFromPointer(errorPointer)
    const hasOwnContextArea = widget.contextArea !== undefined
    const ownContextArea = widget.contextArea === "none" ? null : widget.contextArea
    const effectiveContextArea = hasOwnContextArea
        ? (ownContextArea || null)
        : inheritedContextArea
    const ownsContextArea =
        hasOwnContextArea &&
        !!effectiveContextArea &&
        effectiveContextArea !== inheritedContextArea
    const commitValue = (newValue) => {
        updateData(newValue, savePointer)
        if (widget.action) {
            triggerAction(widget.action, {widget, value: newValue})
        }
        if (effectiveContextArea) {
            triggerAction("UPDATE_CONTEXTAREA", {contextArea: effectiveContextArea, value: newValue, merkmal})
        }
    }
    const {localValue, setLocalValue} = useDebounceTime(value, commitValue)
    const valuesForDisabled = [disabled, widget?.disabled, checkConditionsForDisabled(widget, getDataFromPointer)]
    const isThisWidgetDisabled = valuesForDisabled.some(Boolean)

    const isVisible =
        checkVisibility(widget, getDataFromPointer) &&
        !checkConditionsForHidden(widget, getDataFromPointer)

    useEffect(() => {
        if (!ownsContextArea) return undefined
        triggerAction("REGISTER_CONTEXTAREA", {contextArea: effectiveContextArea})
        return () => {
            triggerAction("UNREGISTER_CONTEXTAREA", {contextArea: effectiveContextArea})
        }
    }, [ownsContextArea, effectiveContextArea])

    const defaultValue = widgetValue === undefined ? defaultFor(savePointer) : undefined
    const isEmpty = value === null || value === undefined
    useEffect(() => {
        if (!isVisible || !savePointer || defaultValue === undefined || !isEmpty) return
        triggerAction("SEED_DEFAULT", {
            pointer: savePointer,
            value: defaultValue,
            contextArea: effectiveContextArea,
        })
    }, [isVisible, savePointer, defaultValue, isEmpty, effectiveContextArea])

    try {
        return isVisible ? (
            WidgetMap[component]({
                key: widget.id,
                widget,
                value: localValue,
                onValueChange: setLocalValue,
                triggerAction,
                updateData,
                getDataFromPointer,
                error,
                disabled: isThisWidgetDisabled,
                style,
                payload,
                placeholder: widget?.placeholder ?? "",
                contextArea: effectiveContextArea,
            })
        ) : (
            <></>
        )
    } catch (error) {
        console.error(
            "Dieses Widget hat keine gültige Komponente, schaue in der Konsole für mehr Infos",
            component,
            widget,
            error,
        )
        return <>No Widget</>
    }
}

export default Widget
